import api from './api'
import { AxiosResponse } from 'axios'

export interface NavigationState {
  canNavigate: boolean
  currentStep: string
  currentStage: number
  allowedSteps: string[]
  blockedSteps: string[]
  nextAvailableStep: string | null
  redirectTo: string | null
  reason?: string
  status?: string
  completedStages: number[]
  progressByStage: {
    stage: number
    totalSteps: number
    completedSteps: number
    percentage: number
  }[]
}

export interface StepAccessValidation {
  isAccessible: boolean
  reason?: string
  redirectTo?: string
  requiresPreviousSteps?: string[]
}

export interface RecoveryRoute {
  route: string
  timestamp: string
}

class OnboardingNavigationService {
  private navigationStateCache: NavigationState | null = null
  private cacheTimestamp: number = 0
  private readonly CACHE_DURATION = 30000 // 30 segundos

  /**
   * Obtiene el estado de navegación del usuario
   */
  async getNavigationState(forceRefresh = false): Promise<NavigationState> {
    const now = Date.now()

    // Usar cache si es válido y no se fuerza refresh
    if (
      !forceRefresh &&
      this.navigationStateCache &&
      now - this.cacheTimestamp < this.CACHE_DURATION
    ) {
      return this.navigationStateCache
    }

    try {
      const response: AxiosResponse<NavigationState> = await api.get(
        '/api/user-onboarding/navigation/state'
      )

      this.navigationStateCache = response.data
      this.cacheTimestamp = now

      return response.data
    } catch (error) {
      console.error('Error obteniendo estado de navegación:', error)
      throw error
    }
  }

  /**
   * Valida si el usuario puede acceder a un paso específico
   */
  async validateStepAccess(stepKey: string): Promise<StepAccessValidation> {
    try {
      const response: AxiosResponse<StepAccessValidation> = await api.get(
        `/api/user-onboarding/navigation/validate/${stepKey}`
      )
      return response.data
    } catch (error) {
      console.error('Error validando acceso al paso:', error)
      return {
        isAccessible: false,
        reason: 'Error validando acceso',
        redirectTo: '/onboarding',
      }
    }
  }

  /**
   * Obtiene la ruta donde el usuario se quedó para recuperar sesión
   */
  async getRecoveryRoute(): Promise<string> {
    try {
      const response: AxiosResponse<RecoveryRoute> = await api.get(
        '/user-onboarding/navigation/recovery-route'
      )
      
      console.log('🔍 Recovery route response:', response.data)
      
      if (response.data && response.data.route) {
        return response.data.route
      }
      
      console.warn('⚠️ No route in response, falling back to /onboarding/flow-selection')
      return '/onboarding/flow-selection'
    } catch (error) {
      console.error('❌ Error obteniendo ruta de recuperación:', error)
      return '/onboarding/flow-selection'
    }
  }

  /**
   * Guarda el tiempo de sesión al cerrar o cambiar de paso
   */
  async saveSessionTime(stepKey: string): Promise<void> {
    try {
      await api.post('/api/user-onboarding/navigation/save-session-time', {
        stepKey,
      })
    } catch (error) {
      console.error('Error guardando tiempo de sesión:', error)
    }
  }

  /**
   * Invalida el cache de navegación
   */
  invalidateCache(): void {
    this.navigationStateCache = null
    this.cacheTimestamp = 0
  }

  /**
   * Verifica si un paso está bloqueado
   */
  isStepBlocked(stepKey: string): boolean {
    if (!this.navigationStateCache) return true
    return this.navigationStateCache.blockedSteps.includes(stepKey)
  }

  /**
   * Verifica si un paso está permitido
   */
  isStepAllowed(stepKey: string): boolean {
    if (!this.navigationStateCache) return false
    return this.navigationStateCache.allowedSteps.includes(stepKey)
  }

  /**
   * Obtiene el progreso por etapa
   */
  getStageProgress(stage: number): number {
    if (!this.navigationStateCache) return 0

    const stageData = this.navigationStateCache.progressByStage.find(
      s => s.stage === stage
    )

    return stageData?.percentage || 0
  }

  /**
   * Verifica si una etapa está completada
   */
  isStageCompleted(stage: number): boolean {
    if (!this.navigationStateCache) return false
    return this.navigationStateCache.completedStages.includes(stage)
  }

  /**
   * Obtiene el siguiente paso disponible
   */
  getNextAvailableStep(): string | null {
    return this.navigationStateCache?.nextAvailableStep || null
  }

  /**
   * Maneja el guardado de tiempo al desmontar componente
   */
  setupUnloadHandler(stepKey: string): () => void {
    const handler = () => {
      this.saveSessionTime(stepKey)
    }

    // Agregar listener para cuando se cierra la ventana
    window.addEventListener('beforeunload', handler)

    // Retornar función de limpieza
    return () => {
      window.removeEventListener('beforeunload', handler)
      // Guardar tiempo al desmontar componente
      this.saveSessionTime(stepKey)
    }
  }
}

export const onboardingNavigationService = new OnboardingNavigationService()
export default onboardingNavigationService
